/*
 Licensed Materials - Property of IBM
 5737-I23
 Copyright IBM Corp. 2019. All Rights Reserved.
 U.S. Government Users Restricted Rights:
 Use, duplication or disclosure restricted by GSA ADP Schedule
 Contract with IBM Corp.
 */
import React, { useEffect, useState, useCallback } from 'react';

import { Dropdown, MultiSelect } from 'carbon-components-react';

import { asNumber, guessType } from 'react-jsonschema-form/lib/utils';
import { useDirtyInput } from '../../common/useDirtyInput';


const nums = new Set(['number', 'integer']);

function processValue(schema, value, required) {
    // "enum" is a reserved word, so only "type" and "items" can be destructured
    const { type, items } = schema;
    if (value === '') {
        return undefined;
    } else if (type === 'array' && items && nums.has(items.type)) {
        return (required && value.length === 0 ? undefined : value.map(asNumber));
    } else if (type === 'array' && items) {
        return (required && value.length === 0 ? undefined : value);
    } else if (type === 'boolean') {
        return value;
    } else if (type === 'number') {
        return asNumber(value);
    }

    // If type is undefined, but an enum is present, try and infer the type from
    // the enum values
    if (schema.enum) {
        if (schema.enum.every(x => guessType(x) === 'number')) {
            return asNumber(value);
        } else if (schema.enum.every(x => guessType(x) === 'boolean')) {
            return value === 'true';
        }
    }
    return value;
}

function getValue(event, multiple) {
    if (multiple) {
        return event.selectedItems.map(o => o.value);
    } else {
        return event.selectedItem.value;
    }
}

function CarbonDropdownWidget(props) {
    const {
        schema,
        id,
        options,
        formContext,
        multiple,
        onChange,
        value,
        onBlur,
        required
        // onFocus,
        // placeholder,
    } = props;
    // INFO: carbon does not support disabled options in Dropdown
    // const { enumOptions, enumDisabled } = options;
    const { enumOptions } = options;

    const _onChange = (event) => {
        const newValue = getValue(event, multiple);
        onChange(processValue(schema, newValue, required));
    };

    const [inputDirty, __onChange, _onBlur] = useDirtyInput(_onChange, onBlur, value);

    const [invalid, setInvalid] = useState(false);
    useEffect(() => {
        if (formContext && formContext.ignoreDirty) {
            setInvalid(schema.invalid);
        } else {
            setInvalid(inputDirty ? schema.invalid : false);
        }
    }, [formContext, inputDirty, schema.invalid]);

    const stateReducer = useCallback((state, changes) => {
        if (changes.type === '__autocomplete_blur_button__' || changes.type === '__autocomplete_mouseup__') {
            _onBlur();
        }
        return changes;
    }, [_onBlur]);

    const items = enumOptions.map(function ({ value, label }, i) {
        return { id: i, text: label, value: value };
    });

    if (!multiple) {
        const selectedItem = items.find(item => item.value === value);

        return (
            <Dropdown
                id={id}
                //label is the text displayed as default
                label={schema.label ? schema.label : ''}
                // titleText is the label in most other component
                invalid={invalid}
                invalidText={schema.invalid ? schema.invalidText : ''}
                items={items}
                itemToString={item => (item ? item.text : '')}
                onChange={__onChange}
                downshiftProps={{ stateReducer: stateReducer }}
                selectedItem={schema.label ? undefined : selectedItem}
            />
        );
    }
    else {
        const selectedItems = ( value ? items.filter(item => {
            return JSON.stringify(value).includes(JSON.stringify(item.value));
        }) : undefined);

        return (
            <MultiSelect
                id={id}
                useTitleInItem={false}
                label={schema.label ? schema.label : ''}
                invalid={invalid}
                invalidText={schema.invalid ? schema.invalidText : ''}
                onChange={__onChange}
                downshiftProps={{ stateReducer: stateReducer }}
                items={items}
                itemToString={item => (item ? item.text : '')}
                initialSelectedItems={selectedItems}
            />
        );
    }
}

CarbonDropdownWidget.defaultProps = {
    autofocus: false,
};


export { CarbonDropdownWidget };
